/**
 * Green cloth: a true chroma key for people with a real green (or blue) backdrop
 * (Brian's handoff, "Green screen on the computer", log #014: "For people with
 * a real green cloth, a true chroma key (pick the colour, spill suppression)").
 * When it is on it replaces the person cutout, in a browser and in the app.
 *
 * Per pixel, in a WebGL shader (a 2D canvas pass at up to 640 wide where WebGL
 * is missing):
 *   - the colour goes to luma and chroma (YCbCr) and the chroma is divided by
 *     the luma, so a shadowed fold of the cloth reads as the same green as its
 *     lit middle;
 *   - its distance from the key colour, as a share of the key's own strength
 *     (so one Tolerance works for a pale or a deep cloth, green or blue), is
 *     averaged with its four neighbours to quiet camera noise;
 *   - alpha rises from Tolerance over Softness (a smoothstep);
 *   - spill suppression takes the key's hue out of what is left: all of it at
 *     the edge, the Spill share of it inside you. Skin and hair point away from
 *     green, so they are left alone; a green cast on a cheek or a shoulder goes.
 */

export interface KeyOpts {
  /** The backdrop's colour, #rrggbb. */
  color: string
  /** 0..100: how far from the key colour still counts as backdrop. */
  tol: number
  /** 0..100: how wide the soft edge is. */
  soft: number
  /** 0..100: how much of the key's hue is taken out of you. */
  spill: number
}

export const KEY_GREEN = '#2fb04a'
export const KEY_BLUE = '#2c56d6'
export const KEY_DEFAULT: KeyOpts = { color: KEY_GREEN, tol: 40, soft: 30, spill: 60 }

export function hexRgb(h: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim())
  const n = m ? parseInt(m[1], 16) : 0x2fb04a
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
export const rgbHex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')

const MIN_Y = 0.08
/** The key in the shader's terms: chroma over luma, its strength, its direction. */
function keyTerms(hex: string) {
  const [r, g, b] = hexRgb(hex).map((v) => v / 255)
  const y = 0.299 * r + 0.587 * g + 0.114 * b, cb = (b - y) * 0.564, cr = (r - y) * 0.713
  const ny = Math.max(y, MIN_Y), kx = cb / ny, ky = cr / ny, mag = Math.max(0.15, Math.hypot(kx, ky))
  const dl = Math.hypot(cb, cr) || 1
  return { kx, ky, mag, dx: cb / dl, dy: cr / dl }
}
/** The sliders in the shader's terms. */
function sliders(o: KeyOpts) {
  const c = (v: number) => Math.max(0, Math.min(100, v)) / 100
  return { tol: 0.1 + c(o.tol) * 0.8, soft: 0.02 + c(o.soft) * 0.6, spill: c(o.spill) }
}

/** How strongly coloured a sample is (0 grey .. about 1 a saturated green): Auto only keys a real cloth. */
export function chromaOf(hex: string) { return Math.hypot(keyTerms(hex).kx, keyTerms(hex).ky) }

const VS = `attribute vec2 p; varying vec2 v; void main(){ v = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }`
const FS = `precision mediump float;
varying vec2 v;
uniform sampler2D t;
uniform vec2 px;
uniform vec2 key;
uniform float kmag;
uniform vec2 kdir;
uniform float tol;
uniform float soft;
uniform float spill;
vec3 ycc(vec3 c){ float y = dot(c, vec3(0.299, 0.587, 0.114)); return vec3(y, (c.b - y) * 0.564, (c.r - y) * 0.713); }
float dist(vec3 c){ vec3 q = ycc(c); return length(q.yz / max(q.x, ${MIN_Y}) - key) / kmag; }
void main(){
  vec4 c = texture2D(t, v);
  float d = (2.0 * dist(c.rgb)
    + dist(texture2D(t, v + vec2(px.x, 0.0)).rgb) + dist(texture2D(t, v - vec2(px.x, 0.0)).rgb)
    + dist(texture2D(t, v + vec2(0.0, px.y)).rgb) + dist(texture2D(t, v - vec2(0.0, px.y)).rgb)) / 6.0;
  float a = smoothstep(tol, tol + soft, d);
  vec3 q = ycc(c.rgb);
  float s = max(dot(q.yz, kdir), 0.0);
  q.yz -= kdir * s * mix(1.0, spill, a);
  vec3 o = clamp(vec3(q.x + 1.403 * q.z, q.x - 0.344 * q.y - 0.714 * q.z, q.x + 1.773 * q.y), 0.0, 1.0);
  gl_FragColor = vec4(o * a, a);
}`

export class ChromaKeyer {
  /** The keyed picture: you with alpha, the backdrop gone. */
  canvas: HTMLCanvasElement
  /** 'webgl' or '2d': which pass ran. */
  kind: 'webgl' | '2d'
  private gl: WebGLRenderingContext | null = null
  private prog: WebGLProgram | null = null
  private tex: WebGLTexture | null = null
  private u: Record<string, WebGLUniformLocation | null> = {}
  private c2: HTMLCanvasElement | null = null

  constructor() {
    this.canvas = document.createElement('canvas')
    this.kind = this.initGL() ? 'webgl' : '2d'
  }

  private initGL(): boolean {
    try {
      const gl = this.canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false, preserveDrawingBuffer: true })
      if (!gl) return false
      const sh = (type: number, src: string) => {
        const s = gl.createShader(type)!; gl.shaderSource(s, src); gl.compileShader(s)
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader')
        return s
      }
      const p = gl.createProgram()!
      gl.attachShader(p, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(p)
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'link')
      gl.useProgram(p)
      const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf)
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
      const loc = gl.getAttribLocation(p, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
      this.tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, this.tex)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
      for (const n of ['t', 'px', 'key', 'kmag', 'kdir', 'tol', 'soft', 'spill']) this.u[n] = gl.getUniformLocation(p, n)
      this.gl = gl; this.prog = p
      return true
    } catch (e) { console.warn('chroma key: no WebGL, using the 2D pass', e); return false }
  }

  /** Keys a w x h picture (already cropped and mirrored). The result is drawn scaled to w x h. */
  key(src: HTMLCanvasElement, o: KeyOpts): HTMLCanvasElement {
    const w = src.width, h = src.height, k = keyTerms(o.color), s = sliders(o)
    const gl = this.gl
    if (gl && this.prog && !gl.isContextLost()) {
      if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h }
      gl.viewport(0, 0, w, h)
      gl.bindTexture(gl.TEXTURE_2D, this.tex)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
      gl.uniform1i(this.u.t, 0); gl.uniform2f(this.u.px, 1 / w, 1 / h)
      gl.uniform2f(this.u.key, k.kx, k.ky); gl.uniform1f(this.u.kmag, k.mag); gl.uniform2f(this.u.kdir, k.dx, k.dy)
      gl.uniform1f(this.u.tol, s.tol); gl.uniform1f(this.u.soft, s.soft); gl.uniform1f(this.u.spill, s.spill)
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
      return this.canvas
    }
    return this.key2d(src, k, s)
  }

  /** The same sums on the CPU, at up to 640 wide. */
  private key2d(src: HTMLCanvasElement, k: ReturnType<typeof keyTerms>, s: ReturnType<typeof sliders>): HTMLCanvasElement {
    const f = Math.min(1, 640 / src.width), w = Math.max(2, Math.round(src.width * f)), h = Math.max(2, Math.round(src.height * f))
    if (!this.c2 || this.c2.width !== w || this.c2.height !== h) { this.c2 = document.createElement('canvas'); this.c2.width = w; this.c2.height = h }
    const g = this.c2.getContext('2d', { willReadFrequently: true })!
    g.globalCompositeOperation = 'copy'; g.drawImage(src, 0, 0, w, h); g.globalCompositeOperation = 'source-over'
    const id = g.getImageData(0, 0, w, h), d = id.data, n = w * h
    const dist = new Float32Array(n)
    for (let i = 0, j = 0; i < n; i++, j += 4) {
      const r = d[j] / 255, gg = d[j + 1] / 255, b = d[j + 2] / 255, y = 0.299 * r + 0.587 * gg + 0.114 * b, ny = y > MIN_Y ? y : MIN_Y
      dist[i] = Math.hypot(((b - y) * 0.564) / ny - k.kx, ((r - y) * 0.713) / ny - k.ky) / k.mag
    }
    for (let i = 0, j = 0; i < n; i++, j += 4) {
      const x = i % w, l = x > 0 ? i - 1 : i, rr = x < w - 1 ? i + 1 : i, u = i >= w ? i - w : i, dn = i + w < n ? i + w : i
      const dd = (2 * dist[i] + dist[l] + dist[rr] + dist[u] + dist[dn]) / 6
      let a = (dd - s.tol) / s.soft; a = a < 0 ? 0 : a > 1 ? 1 : a; a = a * a * (3 - 2 * a)
      const r = d[j] / 255, gg = d[j + 1] / 255, b = d[j + 2] / 255, y = 0.299 * r + 0.587 * gg + 0.114 * b
      let cb = (b - y) * 0.564, cr = (r - y) * 0.713
      const sp = cb * k.dx + cr * k.dy
      if (sp > 0) { const m = sp * (1 + (s.spill - 1) * a); cb -= k.dx * m; cr -= k.dy * m }
      // a 2D canvas takes straight (not premultiplied) alpha
      d[j] = (y + 1.403 * cr) * 255; d[j + 1] = (y - 0.344 * cb - 0.714 * cr) * 255; d[j + 2] = (y + 1.773 * cb) * 255; d[j + 3] = a * 255
    }
    g.putImageData(id, 0, 0)
    return this.c2
  }

  close() { try { this.gl?.getExtension('WEBGL_lose_context')?.loseContext() } catch { /* */ } this.gl = null }
}
